import React, { useEffect, useState } from 'react';
import { Input } from './ui/input';
import { toast } from 'sonner';
import CustomDropdown from './ui/custom-dropdown';
import api from '../utils/api';

const CUSTOM = '__custom__';
let cache = null;

const fetchServedLocations = () => {
  if (!cache) {
    cache = api
      .get('/platform-config/served-locations')
      .then((r) => r.data.locations || [])
      .catch(() => {
        cache = null;
        return [];
      });
  }
  return cache;
};

/**
 * Strict served-city dropdown for the service wizard — cities come from the
 * platform's served-locations source of truth (same list as weather presets).
 * "Other" sends a new-city request to the BookingFlows team instead of free text.
 */
export const ServiceCitySelect = ({ isUAE, value, onChange, onLocationChange }) => {
  const [locations, setLocations] = useState([]);
  const [customMode, setCustomMode] = useState(false);
  const [requestCity, setRequestCity] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let dead = false;
    fetchServedLocations().then((locs) => { if (!dead) setLocations(locs); });
    return () => { dead = true; };
  }, []);

  const country = isUAE ? 'UAE' : 'USA';
  const served = locations.filter((l) => l.country === country);
  const options = [
    ...served.map((l) => ({ value: l.city, label: l.label })),
    { value: CUSTOM, label: 'Other — request a new city…' },
  ];

  const pick = (v) => {
    if (v === CUSTOM) {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    onChange(v);
    const loc = served.find((l) => l.city === v);
    if (loc && onLocationChange) onLocationChange(loc);
  };

  const sendRequest = async () => {
    const city = requestCity.trim();
    if (!city) { toast.error('Type the city you want us to serve'); return; }
    setSending(true);
    try {
      await api.post('/platform-config/city-requests', {
        city,
        organizationId: localStorage.getItem('organizationId'),
      });
      setSent(true);
      toast.success(`Request sent — we'll review ${city} and get back to you`);
    } catch {
      toast.error('Could not send the request — please try again');
    }
    setSending(false);
  };

  return (
    <div>
      <CustomDropdown
        value={customMode ? CUSTOM : (value || '')}
        onChange={pick}
        options={options}
        placeholder={isUAE ? 'Select a city (e.g. Dubai)' : 'Select a city (e.g. Austin)'}
        testId="service-city-select"
      />
      {customMode && (
        <div className="mt-2 space-y-1.5">
          <div className="flex gap-2">
            <Input
              value={requestCity}
              onChange={(e) => { setRequestCity(e.target.value); setSent(false); }}
              placeholder="Which city should we add?"
              data-testid="service-city-request-input"
            />
            <button
              type="button"
              onClick={sendRequest}
              disabled={sending || sent}
              data-testid="service-city-request-btn"
              className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              {sent ? 'Sent ✓' : sending ? 'Sending…' : 'Request'}
            </button>
          </div>
          <p className="text-xs text-amber-600 font-light">
            We only list cities we currently serve — your request goes straight to our team.
            Pick a served city above to save this service.
          </p>
        </div>
      )}
    </div>
  );
};

export default ServiceCitySelect;
